import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ModalWrapper } from "@msuite/ui/modal";
import { type QueryKey, useQueryClient } from "@msuite/ui/query";
import { Fragment } from "react";
import {
	ProjectsFilter,
	type ProjectsFilterController,
} from "@/components/projects/projects-filter/projects-filter";
import { useCitadel } from "@/hooks/use-citadel";
import { AddProjectModal } from "@/modals/projects/add-project-modal";
import type { AddProjectModalProps } from "@/modals/projects/add-project-modal/add-project-modal";

/** Props Interface */
interface ProjectsMenuProps extends Pick<AddProjectModalProps, "defaultValues"> {
	queryKey: QueryKey;
	controller: ProjectsFilterController;
}

export function ProjectsMenu({ queryKey, defaultValues, controller }: ProjectsMenuProps) {
	/** Hooks */
	const { t } = useTranslation();
	const addProjectModal = useDisclosure();
	const qc = useQueryClient();
	const { citadel } = useCitadel();

	/** Functions */
	async function handleOnRefetch() {
		await qc.invalidateQueries({ queryKey });
	}

	async function handleOnComplete() {
		await handleOnRefetch();
	}

	/** Computed */
	// Three states rather than two: a rule list still in flight is not a denial, so the control
	// stays usable while it loads and only a definitive refusal disables it. Same guard as `Can`
	// documents.
	const createIsDenied = !!citadel && citadel.cannot("create", "Project");

	/** Render */
	return (
		<Fragment>
			<Button
				size="xs"
				icon={<Icon name="Plus" />}
				variant="primary"
				onClick={addProjectModal.onOpen}
				data-cy="projects-add-project-button"
				isDisabled={createIsDenied}
			>
				{t("projects.add_project")}
			</Button>
			<ProjectsFilter controller={controller} />
			<ModalWrapper>
				{addProjectModal.isOpen && (
					<AddProjectModal
						isOpen={addProjectModal.isOpen}
						onClose={addProjectModal.onClose}
						onComplete={handleOnComplete}
						defaultValues={defaultValues}
					/>
				)}
			</ModalWrapper>
		</Fragment>
	);
}
